import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MyOrdersSEO } from '../components/SEO';
import { toast } from 'sonner';
import { Package, Clock, CheckCircle, FileText, ArrowRight, AlertCircle, Share2, Newspaper } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  created: { label: 'Created', color: 'bg-[#333333] text-[#a3a3a3]', icon: Clock },
  pending_payment: { label: 'Pending Payment', color: 'bg-[#3d3a1a] text-[#fbbf24]', icon: Clock },
  paid: { label: 'Paid', color: 'bg-[#1a3d1a] text-[#4ade80]', icon: CheckCircle },
  payment_failed: { label: 'Payment Failed', color: 'bg-[#3d1a1a] text-[#f87171]', icon: AlertCircle },
  content_received: { label: 'Content Received', color: 'bg-[#1a2a3d] text-[#60a5fa]', icon: FileText },
  in_progress: { label: 'In Progress', color: 'bg-[#2a1a3d] text-[#a78bfa]', icon: Package },
  under_review: { label: 'Under Review', color: 'bg-[#3d2a1a] text-[#fb923c]', icon: Clock },
  submitted_to_publisher: { label: 'Submitted to Publisher', color: 'bg-[#1a1a3d] text-[#818cf8]', icon: Package },
  live: { label: 'Live', color: 'bg-[#1a3d1a] text-[#4ade80]', icon: CheckCircle },
  published: { label: 'Published', color: 'bg-[#1a3d1a] text-[#4ade80]', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-[#D4AF37] text-[#1a1a1a]', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-[#333333] text-[#737373]', icon: AlertCircle },
  refunded: { label: 'Refunded', color: 'bg-[#3d2a1a] text-[#fb923c]', icon: AlertCircle }
};

const itemTypeConfig = {
  publication: { label: 'Digital PR', color: 'bg-blue-100 text-blue-700', icon: FileText },
  social_page: { label: 'Social Media', color: 'bg-pink-100 text-pink-700', icon: Share2 },
  print_media: { label: 'Print Media', color: 'bg-amber-100 text-amber-700', icon: Newspaper },
  bundle: { label: 'Bundle', color: 'bg-purple-100 text-purple-700', icon: Package }
};

const MyOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to view your orders');
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusInfo = (status) => {
    return statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: Package };
  };

  const getItemTypeInfo = (itemType) => {
    return itemTypeConfig[itemType] || itemTypeConfig.publication;
  };

  const getOrderItemName = (order) => {
    return order.item_name || order.publication_name || 'Order Item';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <MyOrdersSEO />
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-4 text-[#1a1a1a]">My Orders</h1>
          <p className="font-manrope text-base text-[#737373]">
            Track and manage your media coverage orders
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16 border border-[#e5e5e5] bg-[#fafafa]">
            <Package className="w-16 h-16 mx-auto text-[#333333] mb-6" />
            <h2 className="font-playfair text-2xl font-bold mb-4 text-[#1a1a1a]">No Orders Yet</h2>
            <p className="font-manrope text-[#737373] mb-8">
              Start by browsing our publications and placing your first order.
            </p>
            <Link
              to="/marketplace"
              data-testid="browse-publications-link"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#D4AF37] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all duration-300"
            >
              Browse Publications
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => {
              const statusInfo = getStatusInfo(order.order_status);
              const StatusIcon = statusInfo.icon;
              const itemTypeInfo = getItemTypeInfo(order.item_type);
              const ItemTypeIcon = itemTypeInfo.icon;
              
              return (
                <div
                  key={order.id}
                  data-testid={`order-card-${order.id}`}
                  className="border border-[#e5e5e5] bg-[#fafafa] p-6 hover:border-[#D4AF37] transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <h3 className="font-playfair text-xl font-bold text-[#1a1a1a]">
                          {getOrderItemName(order)}
                        </h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-manrope rounded ${itemTypeInfo.color}`}>
                          <ItemTypeIcon className="w-3 h-3" />
                          {itemTypeInfo.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-manrope uppercase tracking-wider ${statusInfo.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Order ID</span>
                          <span className="font-mono text-sm text-[#1a1a1a]">{order.id.substring(0, 8).toUpperCase()}</span>
                        </div>
                        <div>
                          <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Package</span>
                          <span className="font-manrope text-[#737373]">{order.package_name || '-'}</span>
                        </div>
                        <div>
                          <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Amount</span>
                          <span className="font-manrope font-semibold text-[#1a1a1a]">₹{order.total_amount?.toLocaleString('en-IN')}</span>
                        </div>
                        <div>
                          <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Date</span>
                          <span className="font-manrope text-[#737373]">{formatDate(order.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      {order.payment_status === 'paid' && order.order_status === 'paid' && order.item_type === 'publication' && (
                        <Link
                          to={`/order/${order.id}/submit`}
                          data-testid={`submit-content-btn-${order.id}`}
                          className="px-6 py-3 bg-[#D4AF37] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest text-center hover:bg-[#B8960F] transition-all duration-300"
                        >
                          Submit Content
                        </Link>
                      )}
                      <Link
                        to={`/order/${order.id}`}
                        data-testid={`track-order-btn-${order.id}`}
                        className="px-6 py-3 border border-[#e5e5e5] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest text-center hover:border-[#D4AF37] transition-all duration-300"
                      >
                        Track Order
                      </Link>
                    </div>
                  </div>
                  
                  {order.live_url && (
                    <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
                      <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-2">Published Article</span>
                      <a 
                        href={order.live_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-manrope text-sm text-[#D4AF37] hover:underline break-all"
                      >
                        {order.live_url}
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default MyOrders;
